/**
 * `TelegramSetupWizard` — 5-step modal that walks the lawyer through
 * pairing a Telegram channel from scratch (or rotating an existing bot
 * token).
 *
 * Backend contract: `channel-wizards-lexruntime-handback-2026-06-30.md`.
 * Endpoints: `POST /channels/telegram/pair`, `POST /channels/telegram/test`.
 * `pair` is idempotent upsert (first-pair AND rotate use the same path).
 *
 * Steps:
 *   1. **Intro** — inline BotFather cheat-sheet (verbatim from
 *      `login-credentials.md` §"BotFather cheat-sheet") + "Next".
 *   2. **Paste token** — masked field with show/hide toggle. Client-side
 *      regex hint (`<digits>:<letters>` at least 35 chars in the letters
 *      part). Server-side validation is authoritative (see error surface).
 *   3. **Verify** — fires `pairTelegramChannel({botToken})`. Runtime calls
 *      `bot.getMe()` — this IS the auth check. On success, show identity
 *      + fingerprint. On error, surface inline + let user retry.
 *   4. **Test round-trip** — fires `testChannel("telegram")`. Runtime DMs
 *      the most-recently-active peer. `400 NO_TEST_TARGET` guides the
 *      lawyer: "DM @<username> and press START, then try again." Optional
 *      — the lawyer can skip and finish.
 *   5. **Done** — ⚠ CORRECTED 2026-09-13. This said the pair endpoint
 *      "always returns `requiresRoll: true`" and that the step tells the
 *      lawyer his assistant restarts. Both were wrong, and this was the
 *      THIRD site carrying that retracted claim (the other two were in
 *      `lx-openclaw-config.ts`; a two-site fix would have left this one).
 *      A Telegram pair now takes effect with NO ROLL in the ordinary case
 *      (`requiresRoll` FALSE, measured — see that file's docblock), and
 *      `handleApply` never restarted anything: it calls `onApplied ??
 *      settings.refresh`. So the step now says what is TRUE for the
 *      outcome it actually got, and never claims a restart it did not
 *      perform (DS-47 47.D).
 *
 * Cancel is available at every step. Cancelling BEFORE step 3 (pair) is
 * a pure local dismiss (no server state changed). Cancelling AFTER step
 * 3 leaves the token IN PLACE — the pair already happened; the lawyer
 * can complete the roll later or disconnect via the row's own button.
 */

import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import {
  AlertTriangle,
  ArrowRight,
  Check,
  Copy,
  Eye,
  EyeOff,
  Loader2,
  RefreshCw,
  Send,
  X,
} from "lucide-react";
import {
  pairTelegramChannel,
  testChannel,
  LxOpenclawConfigError,
  type ChannelPairTelegramResponse,
  type ChannelTestResponse,
} from "@/api/lx-openclaw-config";
import { useToastStore } from "@/stores/useToastStore";
import { useOpenclawSettingsOptional } from "./OpenclawSettingsTabs";
import { cn } from "@/lib/utils";

/**
 * ⚖ Which of the three done-step outcomes a pair actually produced.
 *
 * Pure and exported so the FALLBACK DIRECTION is unit-testable — it is the one
 * part of this step that has already been wrong once, and no interaction test
 * can reach the rest (lexide's vitest is `node`/SSR-only).
 *
 * 🚨 **An ABSENT `activation` means REFUSED, not PENDING.** This is the
 * correction, and the reasoning is about the population rather than about
 * uncertainty in the abstract:
 *
 * The current runtime spreads `activation` unconditionally on its single
 * success return (`channel-native-handlers.ts:644-655`), so an absent object is
 * not a current runtime being quiet — it is genuinely an OLD one. And the build
 * immediately before Option A hard-coded `requiresRoll: true` with no activation
 * object at all (`9a9922928~1:562`), because that plugin bound the token at
 * gateway_start and a mid-session write did NOT hot-reload.
 *
 * ⇒ On that runtime the bot **cannot** "come online shortly". It comes online on
 * the next restart, full stop — which is exactly what `refused` says: saved, not
 * live, takes effect on the next restart. Three for three.
 *
 * ⚠ My first cut fell back to `pending` as "the honest middle", which reached
 * for uncertainty in general without asking what the uncertain population IS.
 * Once named it is not uncertain at all. Found by `lexruntime-changes`,
 * 2026-09-13; it reintroduced the very 47.D violation this step had just removed,
 * in the opposite direction, in the same commit.
 *
 * ⚠ Timely, not theoretical: a SOFT fleet roll is idle-deferred, so a busy row
 * keeps the old image — and busy lawyers are disproportionately the ones pairing.
 *
 * ⚠ Limit preserved from the report: the immediate parent of `9a9922928` was
 * checked, not every historical build. An intermediate revision sending
 * `activation` with other semantics is not ruled out.
 */
export function doneOutcome(
  requiresRoll: boolean,
  activationState: "applied" | "pending" | "refused" | undefined,
): "Active" | "Pending" | "Refused" {
  if (!requiresRoll) return "Active";
  // ONLY an explicit `pending` earns the softer copy. Absent ⇒ old runtime ⇒
  // Refused, which is true of it.
  return activationState === "pending" ? "Pending" : "Refused";
}

export interface TelegramSetupWizardProps {
  open: boolean;
  onClose: () => void;
  /** Set when opening the wizard as a ROTATE flow (already-connected
   *  channel). Used only for i18n copy ("Trocar token" vs "Conectar").
   *  Backend semantics are identical (upsert). */
  isRotation?: boolean;
  /** Post-apply callback. The wizard is normally hosted inside
   *  `<OpenclawSettingsTabs/>`, where step 5's apply re-fetches the openclaw
   *  config via the settings-context `refresh`. When embedded OUTSIDE that
   *  shell (the channel-connect dialog on the agent tab) there is no such
   *  context, so the host injects what "apply" should do here — e.g. re-read
   *  the channel-endpoint state. Takes precedence over the settings context
   *  when both are present. */
  onApplied?: () => void | Promise<void>;
}

type WizardStep = 1 | 2 | 3 | 4 | 5;

const TOKEN_REGEX = /^\d+:[A-Za-z0-9_-]{35,}$/;

export function TelegramSetupWizard({
  open,
  onClose,
  isRotation,
  onApplied,
}: TelegramSetupWizardProps) {
  const { t } = useTranslation();
  // Optional: null when embedded outside the settings shell. `onApplied` (if
  // supplied by the host) wins; otherwise fall back to the settings-context
  // refresh so the in-settings call sites behave exactly as before.
  const settings = useOpenclawSettingsOptional();
  const addToast = useToastStore((s) => s.addToast);

  const [step, setStep] = useState<WizardStep>(1);
  const [token, setToken] = useState("");
  const [showToken, setShowToken] = useState(false);
  const [pairing, setPairing] = useState(false);
  const [pairResult, setPairResult] = useState<ChannelPairTelegramResponse | null>(null);
  const [pairError, setPairError] = useState<string | null>(null);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<ChannelTestResponse | null>(null);
  const [testError, setTestError] = useState<string | null>(null);
  const [applying, setApplying] = useState(false);
  const tokenInputRef = useRef<HTMLInputElement>(null);

  // Reset all state on dialog open so a re-open starts fresh (avoids
  // showing the previous run's identity/error/etc when the user comes
  // back to pair another bot).
  useEffect(() => {
    if (open) {
      setStep(1);
      setToken("");
      setShowToken(false);
      setPairResult(null);
      setPairError(null);
      setTestResult(null);
      setTestError(null);
      setPairing(false);
      setTesting(false);
      setApplying(false);
    }
  }, [open]);

  // Auto-focus the token input when entering step 2.
  useEffect(() => {
    if (open && step === 2) {
      window.setTimeout(() => tokenInputRef.current?.focus(), 80);
    }
  }, [open, step]);

  if (!open) return null;

  const tokenLooksValid = TOKEN_REGEX.test(token.trim());

  async function handleVerify() {
    if (!tokenLooksValid || pairing) return;
    setPairing(true);
    setPairError(null);
    // 🚨 ADVANCE BEFORE THE AWAIT, and this line is the whole fix.
    //
    // Step 3 IS the verify-RESULT step: `VerifyResultStep` renders all three
    // outcomes — in-progress, error, success. `setStep(3)` used to sit after the
    // await INSIDE the try, so it ran only on success. On failure the wizard
    // stayed on step 2, and the sole renderer of `pairError` was never mounted:
    // the state was set, the mapping was right, the copy existed, and the lawyer
    // saw NOTHING. The in-progress branch was unreachable for the same reason.
    //
    // Measured live by `tester-galvao` 2026-09-13: two 400s from the pair
    // endpoint (2758ms and 199ms), and the dialog's entire visible text was
    // unchanged both times — the only observable effect was the button becoming
    // clickable again, which is `setPairing(false)` in the `finally`, the one
    // state change a MOUNTED component was reading.
    //
    // ⚠ This fires on EVERY failure, not just a bad token — a network error, a
    // dead runtime, an expired session and a Telegram outage all land in the same
    // catch. The lawyer got a button that silently did nothing, however it failed.
    //
    // Getting back to edit the token is already built: the error branch renders
    // `onRetry` → `setStep(2)`, and the footer's primary is gated on `pairResult`
    // so no "next" appears over a failure.
    setStep(3);
    try {
      const result = await pairTelegramChannel({ botToken: token.trim() });
      setPairResult(result);
    } catch (err) {
      setPairError(mapPairError(err, t));
    } finally {
      setPairing(false);
    }
  }

  async function handleTest() {
    if (testing) return;
    setTesting(true);
    setTestError(null);
    setTestResult(null);
    try {
      const r = await testChannel("telegram");
      setTestResult(r);
    } catch (err) {
      setTestError(mapTestError(err, t));
    } finally {
      setTesting(false);
    }
  }

  async function handleApply() {
    // Trigger the existing runtime-upgrade path so the native plugin
    // re-materializes with the newly-written bot-token. The Settings
    // shell already surfaces upgrade UX; we call it and close. When
    // embedded outside the shell, `onApplied` re-reads the host's own
    // state (the channel-endpoint status) instead.
    setApplying(true);
    try {
      const applied = onApplied ?? settings?.refresh;
      if (applied) await applied();
      // ⚖ Only announce a restart where one is actually owed. In the ordinary
      // case the pair is already live (`requiresRoll` false), and this toast
      // used to assert "seu assistente reiniciará em instantes" unconditionally
      // — a restart that neither this handler nor the runtime performs.
      if (doneOutcome(pairResult?.requiresRoll ?? true, pairResult?.activation?.state) === "Refused") {
        addToast({
          kind: "info",
          text: t("settings.openclaw.channels.wizard.telegram.applyingRestart"),
          autoDismissMs: 6000,
        });
      }
      onClose();
    } finally {
      setApplying(false);
    }
  }

  return (
    <>
      <button
        type="button"
        aria-label={t("settings.openclaw.channels.wizard.close")}
        onClick={pairing || applying ? undefined : onClose}
        disabled={pairing || applying}
        className={cn(
          "fixed inset-0 z-40 cursor-default bg-slate-900/50",
          (pairing || applying) && "cursor-wait",
        )}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="tg-wizard-title"
        data-testid="telegram-setup-wizard"
        data-step={step}
        className="fixed left-1/2 top-1/2 z-50 flex max-h-[90vh] w-[min(640px,94vw)] -translate-x-1/2 -translate-y-1/2 flex-col overflow-hidden rounded-xl border border-slate-200 bg-white shadow-2xl dark:border-coffee-700 dark:bg-coffee-900"
      >
        {/* Header */}
        <header className="flex shrink-0 items-center gap-2.5 border-b border-slate-200 px-4 py-3 dark:border-coffee-700/70">
          <Send size={14} className="shrink-0 text-gold-700 dark:text-gold-400" aria-hidden />
          <div className="min-w-0 flex-1">
            <h2 id="tg-wizard-title" className="truncate text-sm font-semibold text-coffee-800 dark:text-cream-100">
              {isRotation
                ? t("settings.openclaw.channels.wizard.telegram.rotateTitle")
                : t("settings.openclaw.channels.wizard.telegram.title")}
            </h2>
            <p className="truncate text-[11px] text-slate-500 dark:text-cream-400">
              {t("settings.openclaw.channels.wizard.stepOf", { current: step, total: 5 })}
            </p>
          </div>
          <button
            type="button"
            onClick={pairing || applying ? undefined : onClose}
            disabled={pairing || applying}
            aria-label={t("settings.openclaw.channels.wizard.close")}
            className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600 disabled:cursor-wait disabled:opacity-50 dark:hover:bg-coffee-800 dark:hover:text-cream-300"
          >
            <X size={14} />
          </button>
        </header>

        {/* Body — scrollable */}
        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4 text-sm text-slate-700 dark:text-cream-200">
          {step === 1 && <IntroStep t={t} />}
          {step === 2 && (
            <PasteTokenStep
              t={t}
              token={token}
              onChange={setToken}
              showToken={showToken}
              onToggleShow={() => setShowToken((v) => !v)}
              tokenValid={tokenLooksValid}
              inputRef={tokenInputRef}
              onEnter={handleVerify}
            />
          )}
          {step === 3 && (
            <VerifyResultStep
              t={t}
              pairing={pairing}
              pairError={pairError}
              pairResult={pairResult}
              onRetry={() => setStep(2)}
            />
          )}
          {step === 4 && (
            <TestStep
              t={t}
              testing={testing}
              testResult={testResult}
              testError={testError}
              identity={pairResult?.identity}
              onTest={handleTest}
            />
          )}
          {step === 5 && (
            <DoneStep
              t={t}
              identity={pairResult?.identity}
              applying={applying}
              // ⚠ Default TRUE when there is no pairResult: the honest default is
              // "not known to be live", never "live". Absent ⇒ pending copy.
              requiresRoll={pairResult?.requiresRoll ?? true}
              activationState={pairResult?.activation?.state}
            />
          )}
        </div>

        {/* Footer navigation */}
        <footer className="flex shrink-0 items-center justify-between gap-3 border-t border-slate-200 bg-slate-50 px-4 py-2.5 dark:border-coffee-700/70 dark:bg-coffee-950/40">
          <button
            type="button"
            onClick={pairing || applying ? undefined : onClose}
            disabled={pairing || applying}
            data-testid="tg-wizard-cancel"
            className="rounded px-3 py-1.5 text-[12px] text-slate-500 hover:bg-slate-200 disabled:opacity-50 dark:text-slate-400 dark:hover:bg-coffee-800"
          >
            {step === 5
              ? t("settings.openclaw.channels.wizard.close")
              : t("settings.openclaw.channels.wizard.cancel")}
          </button>

          <div className="flex items-center gap-2">
            {step === 1 && (
              <StepButton primary onClick={() => setStep(2)}>
                {t("settings.openclaw.channels.wizard.telegram.startPairing")}
                <ArrowRight size={12} aria-hidden />
              </StepButton>
            )}
            {step === 2 && (
              <StepButton
                primary
                onClick={handleVerify}
                disabled={!tokenLooksValid || pairing}
              >
                {pairing ? (
                  <Loader2 size={12} aria-hidden className="animate-spin" />
                ) : (
                  <Check size={12} aria-hidden />
                )}
                {t("settings.openclaw.channels.wizard.telegram.verifyToken")}
              </StepButton>
            )}
            {step === 3 && pairResult && (
              <StepButton primary onClick={() => setStep(4)}>
                {t("settings.openclaw.channels.wizard.telegram.testNext")}
                <ArrowRight size={12} aria-hidden />
              </StepButton>
            )}
            {step === 4 && (
              <StepButton primary onClick={() => setStep(5)}>
                {t("settings.openclaw.channels.wizard.telegram.applyNext")}
                <ArrowRight size={12} aria-hidden />
              </StepButton>
            )}
            {step === 5 && (
              <StepButton primary onClick={handleApply} disabled={applying}>
                {applying ? (
                  <Loader2 size={12} aria-hidden className="animate-spin" />
                ) : (
                  <RefreshCw size={12} aria-hidden />
                )}
                {/* ⚖ The button must not promise a restart this handler does not
                    perform. `handleApply` calls `onApplied ?? settings.refresh`;
                    it has never restarted anything. Only the REFUSED outcome
                    genuinely needs one, and even there the wizard does not do it
                    — so the restart wording is confined to the body copy that
                    explains it, and the button just finishes. */}
                {doneOutcome(pairResult?.requiresRoll ?? true, pairResult?.activation?.state) ===
                "Refused"
                  ? t("settings.openclaw.channels.wizard.telegram.applyRestart")
                  : t("settings.openclaw.channels.wizard.telegram.finish")}
              </StepButton>
            )}
          </div>
        </footer>
      </div>
    </>
  );
}

// ── Step render fragments ──────────────────────────────────────────────

/** Step 1 — the §3.7 BENEFIT screen (assistant-hub S2, §3.8): the operator's
 *  Telegram lines lead; the BotFather cheat-sheet is COLLAPSED beneath "como
 *  funciona" so the mechanism never sits above the why. Steps 2–5 untouched. */
function IntroStep({ t }: { t: TFunction }) {
  return (
    <div className="space-y-3">
      <p className="font-serif-display text-base leading-snug text-coffee-900 dark:text-cream-100">
        {t("channels.benefit.telegram.headline")}
      </p>
      <ul className="space-y-1.5">
        {([1, 2, 3] as const).map((n) => {
          const text = t(`channels.benefit.telegram.proof${n}`, { defaultValue: "" });
          if (!text) return null;
          return (
            <li key={n} className="flex items-start gap-2 text-[13px] text-coffee-700 dark:text-cream-300">
              <span aria-hidden className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-gold-500" />
              <span>{text}</span>
            </li>
          );
        })}
      </ul>
      <details className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-[12px] dark:border-coffee-700/60 dark:bg-coffee-950/40">
        <summary className="cursor-pointer select-none font-semibold text-coffee-800 dark:text-cream-100">
          {t("channels.tgSide1.how")}
        </summary>
        <p className="mb-2 mt-2">{t("settings.openclaw.channels.wizard.telegram.intro.body")}</p>
        <p className="mb-2 font-semibold text-coffee-800 dark:text-cream-100">
          {t("settings.openclaw.channels.wizard.telegram.intro.botfatherTitle")}
        </p>
        <ol className="ml-4 list-decimal space-y-1.5">
          <li>{t("settings.openclaw.channels.wizard.telegram.intro.step1")}</li>
          <li>{t("settings.openclaw.channels.wizard.telegram.intro.step2")}</li>
          <li>{t("settings.openclaw.channels.wizard.telegram.intro.step3")}</li>
          <li>{t("settings.openclaw.channels.wizard.telegram.intro.step4")}</li>
        </ol>
        <p className="mt-2 text-[11px] italic text-slate-500 dark:text-slate-400">
          {t("settings.openclaw.channels.wizard.telegram.intro.rotateHint")}
        </p>
      </details>
    </div>
  );
}

function PasteTokenStep({
  t,
  token,
  onChange,
  showToken,
  onToggleShow,
  tokenValid,
  inputRef,
  onEnter,
}: {
  t: TFunction;
  token: string;
  onChange: (v: string) => void;
  showToken: boolean;
  onToggleShow: () => void;
  tokenValid: boolean;
  inputRef: React.RefObject<HTMLInputElement | null>;
  onEnter: () => void;
}) {
  return (
    <div className="space-y-3">
      <p>{t("settings.openclaw.channels.wizard.telegram.paste.body")}</p>
      <div>
        <label className="mb-1 block text-[11px] font-medium text-slate-500 dark:text-cream-400">
          {t("settings.openclaw.channels.wizard.telegram.paste.label")}
        </label>
        <div className="flex items-center gap-2">
          <input
            ref={inputRef}
            type={showToken ? "text" : "password"}
            value={token}
            onChange={(e) => onChange(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && tokenValid) {
                e.preventDefault();
                onEnter();
              }
            }}
            data-testid="tg-wizard-token-input"
            // ⚠ SYNTHETIC, deliberately. This was "8798946776:AAA…" until
            // 2026-09-13 — a REAL bot id (@ClaudioLexiunBot, another live bot in
            // the same operator's BotFather account). Bot ids are public and the
            // secret half was already dummied, so nothing leaked; the problem is
            // that an example built from a real credential reads as copy-pasted
            // from one, which is a pattern worth not teaching. Keep the SHAPE
            // (digits, colon, opaque tail) and keep the value fake.
            placeholder="1234567890:AAE…"
            spellCheck={false}
            autoCorrect="off"
            autoCapitalize="off"
            // ⚠ `autoComplete="off"` was here and DID NOT WORK. Chromium
            // deliberately ignores `off` on password fields to keep password
            // managers usable, so Chrome autofilled the lawyer's SAVED LEXIUN
            // PASSWORD into this box — measured live 2026-09-13 by
            // tester-galvao, who arrived at "Passo 2 de 5" to find
            // "Lexiun-424242" already in it (the value DevQuickSignIn.tsx:66
            // seeds into the operator's Chrome). Masked, so the lawyer sees
            // only dots and can click "Verificar" on their own password.
            // `new-password` is the form Chromium honours: it declares this a
            // NEW credential, so no stored one is offered. Side effect
            // accepted: Chrome may offer to GENERATE a password here, which is
            // noise but cannot put the wrong secret in the field.
            // The two `data-*` opt-outs are 1Password's and LastPass's.
            // ⚠ Do NOT "tidy" this back to `off`.
            autoComplete="new-password"
            name="telegram-bot-token"
            data-1p-ignore
            data-lpignore="true"
            className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 font-mono text-[13px] tracking-tight text-coffee-800 placeholder:text-slate-400 focus:border-gold-500/60 focus:outline-none focus:ring-2 focus:ring-gold-400/30 dark:border-coffee-700 dark:bg-coffee-950/40 dark:text-cream-100 dark:placeholder:text-slate-500"
          />
          <button
            type="button"
            onClick={onToggleShow}
            aria-label={
              showToken
                ? t("settings.openclaw.channels.wizard.telegram.paste.hide")
                : t("settings.openclaw.channels.wizard.telegram.paste.show")
            }
            className="rounded border border-slate-300 p-2 text-slate-500 hover:bg-slate-100 dark:border-coffee-700 dark:text-cream-300 dark:hover:bg-coffee-800"
          >
            {showToken ? <EyeOff size={14} /> : <Eye size={14} />}
          </button>
        </div>
        {token && !tokenValid && (
          <p
            data-testid="tg-wizard-token-format-hint"
            className="mt-1 flex items-center gap-1 text-[11px] text-amber-700 dark:text-amber-300"
          >
            <AlertTriangle size={11} aria-hidden />
            {t("settings.openclaw.channels.wizard.telegram.paste.formatHint")}
          </p>
        )}
      </div>
    </div>
  );
}

function VerifyResultStep({
  t,
  pairing,
  pairError,
  pairResult,
  onRetry,
}: {
  t: TFunction;
  pairing: boolean;
  pairError: string | null;
  pairResult: ChannelPairTelegramResponse | null;
  onRetry: () => void;
}) {
  if (pairing) {
    return (
      <div className="flex items-center gap-2 text-[13px] text-slate-600 dark:text-cream-300">
        <Loader2 size={14} aria-hidden className="animate-spin" />
        {t("settings.openclaw.channels.wizard.telegram.verify.progress")}
      </div>
    );
  }
  if (pairError) {
    return (
      <div className="space-y-3">
        <div
          data-testid="tg-wizard-verify-error"
          className="flex items-start gap-2 rounded-md border border-red-300 bg-red-50 p-3 text-[13px] text-red-800 dark:border-red-700 dark:bg-red-950/40 dark:text-red-300"
        >
          <AlertTriangle size={14} aria-hidden className="mt-0.5 shrink-0" />
          <span>{pairError}</span>
        </div>
        <button
          type="button"
          onClick={onRetry}
          data-testid="tg-wizard-verify-retry"
          className="rounded-md border border-slate-300 px-3 py-1.5 text-[12px] text-slate-600 hover:bg-slate-100 dark:border-coffee-700 dark:text-cream-300 dark:hover:bg-coffee-800"
        >
          {t("settings.openclaw.channels.wizard.telegram.verify.retry")}
        </button>
      </div>
    );
  }
  if (pairResult) {
    return (
      <div className="space-y-3">
        <div
          data-testid="tg-wizard-verify-success"
          className="flex items-start gap-2 rounded-md border border-emerald-300 bg-emerald-50 p-3 text-[13px] text-emerald-800 dark:border-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-200"
        >
          <Check size={14} aria-hidden className="mt-0.5 shrink-0" />
          <div>
            <p className="font-medium">
              {t("settings.openclaw.channels.wizard.telegram.verify.successTitle")}
            </p>
            <p className="mt-0.5 text-[12px] opacity-90">
              {t("settings.openclaw.channels.wizard.telegram.verify.successBody", {
                username: pairResult.identity.username,
              })}
            </p>
          </div>
        </div>
        <IdentityCard identity={pairResult.identity} fingerprint={pairResult.tokenFingerprint} t={t} />
      </div>
    );
  }
  return null;
}

function TestStep({
  t,
  testing,
  testResult,
  testError,
  identity,
  onTest,
}: {
  t: TFunction;
  testing: boolean;
  testResult: ChannelTestResponse | null;
  testError: string | null;
  identity: ChannelPairTelegramResponse["identity"] | undefined;
  onTest: () => void;
}) {
  return (
    <div className="space-y-3">
      <p>{t("settings.openclaw.channels.wizard.telegram.test.body")}</p>
      <p className="text-[12px] italic text-slate-500 dark:text-slate-400">
        {t("settings.openclaw.channels.wizard.telegram.test.optional")}
      </p>
      <button
        type="button"
        onClick={onTest}
        disabled={testing}
        data-testid="tg-wizard-test-send"
        className="inline-flex items-center gap-1.5 rounded-md border border-gold-500/60 bg-gold-400/15 px-3 py-1.5 text-[12px] font-medium text-gold-700 hover:bg-gold-400/25 hover:text-gold-600 disabled:cursor-wait disabled:opacity-60 dark:border-gold-500/40 dark:text-gold-400"
      >
        {testing ? (
          <Loader2 size={12} aria-hidden className="animate-spin" />
        ) : (
          <Send size={12} aria-hidden />
        )}
        {t("settings.openclaw.channels.wizard.telegram.test.sendButton")}
      </button>
      {testResult && (
        <div
          data-testid="tg-wizard-test-success"
          className="rounded-md border border-emerald-300 bg-emerald-50 p-3 text-[12px] text-emerald-800 dark:border-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-200"
        >
          <p className="font-medium">
            <Check size={12} aria-hidden className="mr-1 inline align-text-bottom" />
            {t("settings.openclaw.channels.wizard.telegram.test.successTitle")}
          </p>
          <p className="mt-1 opacity-90">
            {t("settings.openclaw.channels.wizard.telegram.test.successBody", {
              target: testResult.target,
            })}
          </p>
          <p className="mt-1 font-mono text-[11px] opacity-70">{testResult.message}</p>
        </div>
      )}
      {testError && (
        <div
          data-testid="tg-wizard-test-error"
          className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-[12px] text-amber-800 dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-200"
        >
          <AlertTriangle size={12} aria-hidden className="mt-0.5 shrink-0" />
          <span>
            {testError}
            {identity?.username && (
              <>
                {" "}
                <span className="font-mono">@{identity.username}</span>
              </>
            )}
          </span>
        </div>
      )}
    </div>
  );
}

/**
 * ⚖ The done step says what is TRUE for the outcome it actually got.
 *
 * 🚨 It used to say one thing always: *"Vamos reiniciar seu workspace…"* — a
 * restart claim, in a step whose `handleApply` calls `onApplied ??
 * settings.refresh` and has never restarted anything. Measured 2026-09-13:
 * `requiresRoll` FALSE, `restartCount 0`, same container 4.5h on. So the wizard
 * asserted a restart four times (body, button, toast, spinner caption) and none
 * occurred. That is DS-47 47.D's shape — claiming an action the turn did not
 * take — on the lawyer's own screen.
 *
 * ⚠ `pending` and `refused` arrive as ORDINARY 200s and never reach the
 * `catch`, so they cannot be handled as errors — see
 * `ChannelPairTelegramResponse.activation`. They are the reason the boolean is
 * not enough: "reiniciando para ativar" is TRUE for `refused` and OVERSTATED
 * for `pending`.
 *
 * ⚠ Falls back to the PENDING copy when `activation` is absent (an older
 * runtime that sends `requiresRoll` without it): pending is the honest middle —
 * it neither promises it is live nor asserts a restart is required.
 */
function DoneStep({
  t,
  identity,
  applying,
  requiresRoll,
  activationState,
}: {
  t: TFunction;
  identity: ChannelPairTelegramResponse["identity"] | undefined;
  applying: boolean;
  requiresRoll: boolean;
  activationState: "applied" | "pending" | "refused" | undefined;
}) {
  const bodyKey = `body${doneOutcome(requiresRoll, activationState)}` as const;
  return (
    <div className="space-y-3">
      <div className="flex items-start gap-2 rounded-md border border-emerald-300 bg-emerald-50 p-3 text-[13px] text-emerald-800 dark:border-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-200">
        <Check size={14} aria-hidden className="mt-0.5 shrink-0" />
        <div>
          <p className="font-medium">
            {t("settings.openclaw.channels.wizard.telegram.done.title", {
              username: identity?.username ?? "…",
            })}
          </p>
          <p className="mt-1 text-[12px] opacity-90" data-testid={`tg-wizard-done-${bodyKey}`}>
            {t(`settings.openclaw.channels.wizard.telegram.done.${bodyKey}`)}
          </p>
        </div>
      </div>
      {applying && (
        <div className="flex items-center gap-2 text-[12px] text-slate-600 dark:text-cream-300">
          <Loader2 size={12} aria-hidden className="animate-spin" />
          {t("settings.openclaw.channels.wizard.telegram.done.applying")}
        </div>
      )}
    </div>
  );
}

// ── Small shared pieces ────────────────────────────────────────────────

function StepButton({
  children,
  onClick,
  disabled,
  primary,
}: {
  children: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
  primary?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      data-testid="tg-wizard-step-primary"
      className={cn(
        "inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-[12px] font-medium disabled:cursor-not-allowed disabled:opacity-50",
        primary
          ? "border border-gold-500/60 bg-gold-400/15 text-gold-700 hover:bg-gold-400/25 hover:text-gold-600 dark:border-gold-500/40 dark:text-gold-400"
          : "border border-slate-300 text-slate-600 hover:bg-slate-100 dark:border-coffee-700 dark:text-cream-300 dark:hover:bg-coffee-800",
      )}
    >
      {children}
    </button>
  );
}

function IdentityCard({
  identity,
  fingerprint,
  t,
}: {
  identity: ChannelPairTelegramResponse["identity"];
  fingerprint: string;
  t: TFunction;
}) {
  return (
    <dl className="grid grid-cols-[max-content_1fr] gap-x-3 gap-y-1 rounded-md border border-slate-200 bg-slate-50 p-3 text-[12px] dark:border-coffee-700/60 dark:bg-coffee-950/40">
      <dt className="text-slate-500 dark:text-cream-500">
        {t("settings.openclaw.channels.wizard.telegram.identity.username")}
      </dt>
      <dd className="font-mono text-coffee-800 dark:text-cream-100">
        @{identity.username}
      </dd>
      <dt className="text-slate-500 dark:text-cream-500">
        {t("settings.openclaw.channels.wizard.telegram.identity.id")}
      </dt>
      <dd className="font-mono">{identity.id}</dd>
      {identity.firstName && (
        <>
          <dt className="text-slate-500 dark:text-cream-500">
            {t("settings.openclaw.channels.wizard.telegram.identity.firstName")}
          </dt>
          <dd>{identity.firstName}</dd>
        </>
      )}
      <dt className="text-slate-500 dark:text-cream-500">
        {t("settings.openclaw.channels.wizard.telegram.identity.fingerprint")}
      </dt>
      <dd className="font-mono">
        {fingerprint}{" "}
        <button
          type="button"
          onClick={() => {
            void navigator.clipboard?.writeText(fingerprint);
          }}
          className="ml-1 inline-flex items-center gap-0.5 rounded p-0.5 text-slate-400 hover:text-slate-600 dark:hover:text-cream-300"
          title={t("settings.openclaw.channels.wizard.copy")}
        >
          <Copy size={10} aria-hidden />
        </button>
      </dd>
    </dl>
  );
}

// ── Error mappers ──────────────────────────────────────────────────────

function mapPairError(err: unknown, t: TFunction): string {
  if (err instanceof LxOpenclawConfigError) {
    switch (err.code) {
      case "INVALID_TOKEN_FORMAT":
        return t("settings.openclaw.channels.wizard.telegram.errors.INVALID_TOKEN_FORMAT");
      case "TELEGRAM_UNAUTHORIZED":
        return t("settings.openclaw.channels.wizard.telegram.errors.TELEGRAM_UNAUTHORIZED");
      case "TELEGRAM_UPSTREAM_ERROR":
        return t("settings.openclaw.channels.wizard.telegram.errors.TELEGRAM_UPSTREAM_ERROR");
      case "TELEGRAM_UNREACHABLE":
        return t("settings.openclaw.channels.wizard.telegram.errors.TELEGRAM_UNREACHABLE");
      case "USER_SETTING_DISABLED_BY_ORG":
        return t("settings.openclaw.channels.wizard.telegram.errors.USER_SETTING_DISABLED_BY_ORG");
      default:
        return err.message || t("settings.openclaw.channels.wizard.telegram.errors.UNKNOWN");
    }
  }
  return err instanceof Error ? err.message : String(err);
}

function mapTestError(err: unknown, t: TFunction): string {
  if (err instanceof LxOpenclawConfigError) {
    switch (err.code) {
      case "NO_TEST_TARGET":
        return t("settings.openclaw.channels.wizard.telegram.errors.NO_TEST_TARGET");
      case "SEND_FAILED":
        return t("settings.openclaw.channels.wizard.telegram.errors.SEND_FAILED");
      case "NOT_PAIRED":
        return t("settings.openclaw.channels.wizard.telegram.errors.NOT_PAIRED");
      default:
        return err.message || t("settings.openclaw.channels.wizard.telegram.errors.UNKNOWN");
    }
  }
  return err instanceof Error ? err.message : String(err);
}
